import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { FormControl, Select, MenuItem, CircularProgress } from '@mui/material';
import { ReactComponent as Dropdown } from '../../../assets/images/dropdown-arrow.svg';
import { toast } from 'react-toastify';
import {
  fetchTicketsForExport,
  downloadTicketsJson,
  downloadTicketsCsv,
} from '../../../utils/ticketExport';

const EXPORT_CONTROL_WIDTH = 140;

const menuPaperSx = {
  border: '1px solid #D5DCE1',
  marginTop: '8px',
  width: EXPORT_CONTROL_WIDTH,
  minWidth: EXPORT_CONTROL_WIDTH,
  maxWidth: EXPORT_CONTROL_WIDTH,
  borderRadius: '8px',
  background: '#FFF',
  boxShadow: '0 15px 20px 0 rgba(6, 21, 34, 0.05)',
  boxSizing: 'border-box',
  '& .MuiList-root': {
    padding: '4px',
  },
  '& .MuiMenuItem-root': {
    fontSize: '14px',
    fontFamily: 'Inter',
    padding: '8px 12px',
    color: '#405261',
    borderRadius: '8px',
    minHeight: '36px',
    backgroundColor: '#FFF !important',
    boxSizing: 'border-box',
    '&:hover, &.Mui-selected': {
      color: '#061522',
      backgroundColor: '#F9FAFA !important',
    },
  },
};

const selectSx = {
  height: '40px',
  width: EXPORT_CONTROL_WIDTH,
  fontSize: '14px',
  color: '#405261',
  fontFamily: 'Inter',
  borderRadius: '8px',
  border: '1px solid #D5DCE1',
  background: '#FFF',
  boxShadow: '0 1px 2px 0 rgba(6, 21, 34, 0.05)',
  '&:hover': {
    border: '1px solid #9CA3AF',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '& .MuiSelect-select': {
    padding: '8px 36px 8px 12px',
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiSelect-icon': {
    fontSize: '16px',
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    right: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  '& .MuiSelect-iconOpen': {
    transform: 'translateY(-50%) rotate(180deg)',
  },
};

function TicketExportButton({ buttonLabel, jsonLabel, csvLabel }) {
  const [value, setValue] = useState('');
  const [exporting, setExporting] = useState(false);
  const authToken = useSelector((state) => state.loginToken?.response?.token);
  const userId = useSelector((state) => state.loginToken?.response?.user?._id);
  const businessName = useSelector((state) => state.loginToken?.response?.user?.business_name);

  const exportOptions = [
    { value: 'json', label: jsonLabel },
    { value: 'csv', label: csvLabel },
  ];

  const runExport = async (format) => {
    if (!userId) {
      toast.error('You must be signed in to export tickets.');
      return;
    }
    if (exporting) return;

    setExporting(true);
    const toastId = toast.info('Preparing ticket export…', { autoClose: false });

    try {
      const data = await fetchTicketsForExport(authToken);
      if (format === 'json') {
        downloadTicketsJson(data, businessName);
      } else {
        downloadTicketsCsv(data, businessName);
      }
      const formatLabel = format === 'json' ? 'JSON' : 'CSV';
      toast.update(toastId, {
        render: `Exported ${data.ticketCount} ticket(s) as ${formatLabel}.`,
        type: 'success',
        autoClose: 4000,
      });
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to export tickets.';
      toast.update(toastId, {
        render: message,
        type: 'error',
        autoClose: 5000,
      });
    } finally {
      setExporting(false);
      setValue('');
    }
  };

  const handleChange = (event) => {
    const format = event.target.value;
    if (!format) return;
    setValue(format);
    runExport(format);
  };

  return (
    <div className="ticket-export-actions">
      <FormControl size="small" className="ticket-export-form" sx={{ width: EXPORT_CONTROL_WIDTH }}>
        <Select
          value={value}
          onChange={handleChange}
          disabled={exporting}
          IconComponent={Dropdown}
          displayEmpty
          renderValue={(selected) => {
            if (exporting) {
              return (
                <span className="ticket-export-select-value">
                  <CircularProgress size={14} sx={{ color: '#405261', mr: 0.75 }} />
                  {buttonLabel}
                </span>
              );
            }
            if (!selected) {
              return (
                <span className="ticket-export-select-value ticket-export-placeholder">
                  {buttonLabel}
                </span>
              );
            }
            const opt = exportOptions.find((o) => o.value === selected);
            return (
              <span className="ticket-export-select-value">{opt?.label ?? selected}</span>
            );
          }}
          sx={selectSx}
          MenuProps={{
            autoWidth: false,
            disablePortal: false,
            anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
            transformOrigin: { vertical: 'top', horizontal: 'right' },
            PaperProps: { sx: menuPaperSx },
          }}
        >
          {exportOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );
}

export default TicketExportButton;
